import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  JSX,
  Dispatch as ReactDispatch,
  ReactNode,
  Ref,
  SetStateAction,
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {styled} from 'styled-components';

import {Defined} from '@shared/lib/utils/type_utils';

import {withThemeOverride} from '@shared_web/components/core/_base';
import {Label} from '@shared_web/components/core/label';
import {cssPx, optional, optionalPx, optionalRaw} from '@shared_web/lib/styled_utils';
import {useIsMounted} from '@shared_web/lib/use_is_mounted';
import {usePrevious} from '@shared_web/lib/use_previous';
import {FrontendTheme} from '@shared_web/theme/theme_model';

type Dispatch<T> = ReactDispatch<SetStateAction<T>> | ((val: T) => void);

export type LabelPosition = 'left' | 'center';

export interface InputControlProps<T> {
  value: T;
  syncState?: Dispatch<T>;
  syncStateWithEvent?: (value: T, evt: ChangeEvent<HTMLInputElement>) => void;
  asString?: (value: T) => string;
  fromString?: (value: string) => T;
  noAutoFormat?: boolean;
  forceFormat?: number;
}

export interface InputDisplayProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'style' | 'value' | 'children'> {
  ref?: Ref<HTMLInputElement>;
  label?: string | JSX.Element;
  labelPosition?: LabelPosition;
  noLabelOffset?: boolean;
  focusOnMount?: boolean;
  rowLabel?: boolean;
  icon?: JSX.Element;
}

type InputProps<T> = InputControlProps<T> & InputDisplayProps;

function InputInternal<T>(props: InputProps<T>, ref: Ref<HTMLInputElement>): JSX.Element {
  const {
    width,
    syncState,
    syncStateWithEvent,
    value,
    asString,
    fromString,
    label,
    labelPosition,
    focusOnMount,
    onChange,
    onBlur,
    noAutoFormat = asString !== undefined,
    forceFormat,
    rowLabel,
    icon,
    ...inputProps
  } = props;

  const internalRef = createRef<HTMLInputElement>();
  const inputIconWrapperRef = useRef<HTMLDivElement>(null);
  const [iconWrapperWidth, setIconWrapperWidth] = useState<number>();
  const stringify = useCallback(
    (val: T) => (asString === undefined ? (val === undefined ? '' : String(val)) : asString(val)),
    [asString]
  );
  const [internalStr, setInternalStr] = useState(stringify(value));
  const isMounted = useIsMounted();
  const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    evt => {
      onChange?.(evt);
      if (!isMounted.current) {
        return;
      }
      setInternalStr(evt.currentTarget.value);
      try {
        const val =
          fromString === undefined
            ? (evt.currentTarget.value as unknown as T)
            : fromString(evt.currentTarget.value);
        syncState?.(val);
        syncStateWithEvent?.(val, evt);
      } catch {
        // invalid input, don't call syncState
      }
    },
    [fromString, isMounted, onChange, syncState, syncStateWithEvent]
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    evt => {
      onBlur?.(evt);
      setInternalStr(stringify(value));
    },
    [onBlur, stringify, value]
  );

  const prevForceFormat = usePrevious(forceFormat);
  useEffect(() => {
    if (forceFormat !== undefined && prevForceFormat !== forceFormat) {
      setInternalStr(stringify(value));
    }
  }, [forceFormat, prevForceFormat, stringify, value]);

  useEffect(() => {
    if (!noAutoFormat) {
      setInternalStr(stringify(value));
    }
  }, [noAutoFormat, stringify, value]);

  useEffect(() => {
    const actualRef = ref ?? internalRef;
    if (focusOnMount && typeof actualRef === 'object') {
      actualRef.current?.focus();
    }
  }, [focusOnMount, internalRef, ref]);

  useLayoutEffect(() => {
    if (icon && inputIconWrapperRef.current) {
      const {width} = inputIconWrapperRef.current.getBoundingClientRect();
      setIconWrapperWidth(width);
    }
  }, [icon]);

  const input = (
    <StyledInput
      ref={ref ?? internalRef}
      width={width}
      spellCheck={false}
      autoCapitalize="off"
      onChange={handleInputChange}
      onBlur={handleBlur}
      type="text"
      value={internalStr}
      $iconWidth={iconWrapperWidth}
      {...inputProps}
    />
  );

  const inputAndIcon = icon ? (
    <InputWrapper>
      {input}
      <IconWrapper ref={inputIconWrapperRef}>{icon}</IconWrapper>
    </InputWrapper>
  ) : (
    input
  );

  const $labelPosition = labelPosition ?? 'left';
  if (label !== undefined) {
    const el = (
      <InputLabel value={label} $labelPosition={$labelPosition}>
        {inputAndIcon}
      </InputLabel>
    );
    // Wrap in a <div> (flex column) so that the label is on top of the input (unless `rowLabel` is enabled)
    return rowLabel ? el : <ColumnLabel $labelPosition={$labelPosition}>{el}</ColumnLabel>;
  }

  return inputAndIcon;
}

const BaseInput = withThemeOverride(forwardRef(InputInternal));
BaseInput.displayName = 'Input';

export const Input = BaseInput as <Item>(
  props: InputProps<Item> & {
    themeOverrides?: FrontendTheme;
  }
) => JSX.Element;

// export const Input = forwardRef(
//   BaseInput
// ) as <Item>(
//   props: InputProps<Item> & {
//     themeOverrides?: FrontendTheme;
//   }
// ) => JSX.Element;

type InputTheme = Defined<FrontendTheme['input']>;

export function borderAndBackground(opts: {
  borderWidth?: InputTheme['borderWidth'];
  borderColor?: InputTheme['borderColor'];
  backgroundColor?: InputTheme['backgroundColorActive'];
}): string {
  const {borderWidth, borderColor = 'transparent', backgroundColor = 'transparent'} = opts;
  if (borderColor?.startsWith('linear-gradient')) {
    return `
      border: double ${cssPx(borderWidth ?? 0)}px transparent;
      background-image: linear-gradient(${backgroundColor}, ${backgroundColor}), ${borderColor};
      background-origin: border-box;
      background-clip: padding-box, border-box;
    `;
  }
  return `
      border-style: solid;
      border-width: ${borderWidth}px;
      border-color: ${borderColor};
      background-color: ${backgroundColor};
      background-image: none;
      background-origin: inherit;
      background-clip: inherit;
    `;
}

export const StyledInput = styled.input<{$iconWidth?: number}>`
  display: block;
  ${p => optionalPx('width', p.width)}
  ${p => optionalPx('height', p.theme.input?.height)}
  box-sizing: border-box;

  outline: none;
  padding-right: ${p =>
    p.$iconWidth === undefined ? cssPx(p.theme.input?.paddingRight ?? 0) : `${p.$iconWidth}px`};
  ${p => optionalPx('padding-left', p.theme.input?.paddingLeft)}

  ${p => optional('font-family', p.theme.input?.fontFamily)}
  ${p => optional('font-weight', p.theme.input?.fontWeight)}
  ${p => optionalPx('font-size', p.theme.input?.fontSize)}
  ${p => optional('color', p.theme.input?.textColor)}
  ${p => optionalPx('line-height', p.theme.input?.lineHeight)}
  
  ${p => optionalPx('border-radius', p.theme.input?.borderRadius)}
  ${p =>
    borderAndBackground({
      borderColor: p.theme.input?.borderColor ?? '',
      backgroundColor: p.theme.input?.backgroundColorActive ?? '',
      borderWidth: p.theme.input?.borderWidth ?? 0,
    })}

  &:hover {
    ${p =>
      borderAndBackground({
        borderColor: p.theme.input?.hoverBorderColor,
        backgroundColor: p.theme.input?.backgroundColorHover,
        borderWidth: p.theme.input?.borderWidth,
      })}
  }

  &:active:not([disabled]),
  &:focus:not([disabled]) {
    ${p =>
      borderAndBackground({
        borderColor: p.theme.input?.focusBorderColor,
        backgroundColor: p.theme.input?.backgroundColorFocus,
        borderWidth: p.theme.input?.focusBorderWidth,
      })}
    ${p =>
      optionalRaw(
        p.theme.input?.focusOutlineWidth,
        v => `box-shadow: 0 0 0 ${cssPx(v)} ${p.theme.input?.focusOutlineColor};`
      )}
    ${p => optional('color', p.theme.input?.focusTextColor)}
  }

  &:disabled {
    ${p => optional('color', p.theme.input?.textColorDisabled)}
    box-shadow: none;
    pointer-events: none;
    ${p =>
      borderAndBackground({
        borderColor: p.theme.input?.borderColor,
        backgroundColor: p.theme.input?.backgroundColorDisabled,
        borderWidth: p.theme.input?.borderWidth,
      })}
  }
`;

export const InputLabel = styled(Label)<{$labelPosition: LabelPosition}>`
  display: inline-block;
  line-height: 120%;
  ${p => p.$labelPosition === 'center' && 'text-align: center;'}
  ${p => optionalPx('font-size', p.theme.input?.labelFontSize)}
  ${p => optionalPx('margin-bottom', p.theme.input?.labelMarginBottom)}
  font-weight: 400;
  padding-left: 0;
`;

const ColumnLabel = styled.div<{$labelPosition: LabelPosition}>`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.$labelPosition === 'left' ? 'flex-start' : 'center')};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-right-radius: ${p => cssPx(p.theme.input?.borderRadius ?? 0)};
  border-bottom-right-radius: ${p => cssPx(p.theme.input?.borderRadius ?? 0)};
  overflow: hidden;
`;

//
//
//

export function withInputLabel(
  label: ReactNode | undefined,
  element: ReactNode,
  opts?: {
    labelPosition?: LabelPosition;
    rowLabel?: boolean;
  }
): JSX.Element {
  const {labelPosition} = opts ?? {};
  if (label === undefined) {
    return <>{element}</>;
  }
  return (
    <InputLabel $labelPosition={labelPosition ?? 'left'} value={<>{label}</>}>
      <>{element}</>
    </InputLabel>
  );
}
